import { GuidEmpty } from 'mbs-ui-kit/utils/constants';
import { StorageType } from 'mbs-ui-shared@models';
import { PlanTypesDisplay } from 'mbs-ui-shared@models/PlanTypes.enum';
import { BaseForStepsHelper, WizardHelpersSettings } from 'wizards/helpers/bases/base-for-steps-helper';
import { StepsDataFromPlanHelper } from 'wizards/helpers/steps-data-from-plan-helper/steps-data-from-plan-helper';
import { SupportMethodsForStepsFromPlan } from 'wizards/helpers/support-methods-for-steps-from-plan';
import { IBBFormModel } from 'wizards/models/ibb/ibb-form-model';

export class IBBModelForStepsHelper extends BaseForStepsHelper {
  public static IBBModel: IBBFormModel = null;

  public static IBBModelFromPlan(plan, settings: WizardHelpersSettings = this.DefaultSettings): IBBFormModel {
    const newPlan = plan && plan.plan;
    if (newPlan) {
      this.setThisValues(plan, settings);
      this.setStorageType(settings.mode, newPlan.ConnectionID);
      this.IBBModel = new IBBFormModel(this.isNBF, this.isUnix && !this.isNetBuild);

      this.updatePlanNameAndWhereBackup(newPlan, settings);
      this.IBBModel.whatBackupStep = StepsDataFromPlanHelper.updateWhatBackup(plan, settings.isOffline);
      this.IBBModel.compressionEncryptionStep = StepsDataFromPlanHelper.updateCompressionEncryption(
        newPlan,
        this.isNBF,
        this.isUnix && !this.isNetBuild,
        this.isCreate
      );
      if (!this.isNBF) {
        this.IBBModel.retentionPolicyStep = StepsDataFromPlanHelper.updateRetentionPolicy(newPlan, this.isCreate);
        this.IBBModel.scheduleStep = StepsDataFromPlanHelper.updateSchedule(
          newPlan,
          plan.OverdueAfterSuccess,
          this.isCreate,
          this.isUnix && !this.isNetBuild
        );
      } else {
        this.bucketId = this.accounts && this.isRD ? this.getBucketId(newPlan.Bucket, newPlan.SGAccountID) : newPlan.ConnectionID;
        this.IBBModel.retentionPolicyStep = StepsDataFromPlanHelper.updateNBFRetentionPolicy(
          newPlan,
          SupportMethodsForStepsFromPlan.needShowImmutability(this.storages.CloudStorages, this.bucketId),
          this.isCreate,
          this.isUnix && !this.isNetBuild,
          this.isNBF
        );

        if (this.nbfCanHybrid && !!newPlan.HybridConnections?.length) {
          this.IBBModel.retentionPolicyStep = StepsDataFromPlanHelper.updateNBFHybridRetentionPolicy(
            newPlan,
            this.IBBModel.retentionPolicyStep
          );
        }

        this.IBBModel.archiveConsistencyStep = StepsDataFromPlanHelper.updateArchiveConsistency(newPlan, true);
        this.IBBModel.scheduleStep = StepsDataFromPlanHelper.updateNBFSchedule(
          newPlan,
          plan.OverdueAfterSuccess,
          this.isCreate,
          this.isUnix && !this.isNetBuild,
          settings.isNBF,
          settings.validVersionForSchedule || settings.isRD
        );
      }
      this.updateAdvancedOptionsData(newPlan, settings);
      this.IBBModel.prePostActionsStep = StepsDataFromPlanHelper.updatePrePostActions(newPlan);
      this.IBBModel.AvailableMachinePlans = StepsDataFromPlanHelper.updateAvailableMachinePlans(plan.AvailableMachinePlans, newPlan.ID);
      this.IBBModel.notificationStep = StepsDataFromPlanHelper.updateNotification(newPlan.Notification);
    }
    return this.IBBModel;
  }

  public static updatePlanNameAndWhereBackup(newPlan, settings: WizardHelpersSettings): void {
    this.updatePlanName(newPlan, settings);

    if (!this.accounts) {
      this.updateWhereBackupStepData(newPlan, this.IBBModel, settings);

      return this.updateStorages();
    }

    const id = this.bucketId || this.getBucketId(newPlan.Bucket, newPlan.SGAccountID);

    if (id && id !== newPlan.ConnectionID && !this.storage) this.setStorageType(settings.mode, id);

    this.updateWhereBackupStepData(newPlan, this.IBBModel, settings, id);

    if (id && id !== GuidEmpty) {
      const storage = this.storages.CloudStorages.find((s) => s.ID === id) || this.storages.LocalStorages.find((s) => s.ID === id);
      this.storageType = storage ? storage.StorageType : this.storageType;
    }

    this.updateStorages();
  }

  static updateWhereBackupStepData(newPlan, model: IBBFormModel, settings: WizardHelpersSettings, id = ''): void {
    const isHybrid = this.nbfCanHybrid && !!newPlan.HybridConnections?.length;

    const ConnectionID = isHybrid ? newPlan.HybridConnections?.[0]?.HybridConnectionId : id || newPlan.ConnectionID;
    const HybridID = isHybrid ? id || newPlan.ConnectionID : newPlan.HybridID;

    model.whereBackupStep = StepsDataFromPlanHelper.updateWhereBackup(
      isHybrid ? !!ConnectionID : newPlan.IsHybridBackup,
      HybridID,
      ConnectionID
    );
  }

  public static updateAdvancedOptionsData(newPlan, settings: WizardHelpersSettings): void {
    const advancedOptionsData = StepsDataFromPlanHelper.updateAdvancedOptions(newPlan, this.storage, this.isNBF, settings.agentVersion);
    if (this.isCreate && this.isNBF && !advancedOptionsData.advancedOptionsStep.SyntheticFull) {
      advancedOptionsData.advancedOptionsStep.SyntheticFull = true;
    }
    this.IBBModel.advancedOptionsStep = advancedOptionsData.advancedOptionsStep;
    this.IBBModel.advancedOptionsFlags = advancedOptionsData.advancedOptionsFlags;
  }

  private static updatePlanName(newPlan, settings: WizardHelpersSettings): void {
    if (this.isCreate && settings.agentSimpleUi) {
      this.IBBModel.planNameStep = {
        Name: PlanTypesDisplay[this.planType],
        SavePlanInCloud: newPlan.SavePlanInCloud || this.isNBF,
        valid: true
      };
    } else {
      const name = newPlan.Name || SupportMethodsForStepsFromPlan.generatePlanName(settings.compName, this.planType, this.timeZoneOffset);
      this.IBBModel.planNameStep = StepsDataFromPlanHelper.updatePlanNameStep(name, newPlan.SavePlanInCloud, this.isNBF);
    }
  }

  private static updateStorages(): void {
    if (!this.isRD && this.isNBF) SupportMethodsForStepsFromPlan.getFilteredStoragesByTypes(this.storages, [StorageType.OpenStack]);

    this.IBBModel.Storages = this.storages;
  }
}
